import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Switch } from '../../../../components/ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '../../../../components/ui/command';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { MonitoredUrl } from '../../types/site-monitor';
import { createMonitoredUrl, updateMonitoredUrl } from '../../services/siteMonitorApi';

interface AddEditMonitoredUrlModalProps {
  monitoredUrl: MonitoredUrl | null;
  isOpen: boolean;
  toggle: () => void;
  onAddData: (newData: MonitoredUrl) => void;
  onUpdateData: (newData: MonitoredUrl) => void;
}

const typeOptions = [
  { value: 'LANDING_PAGE', label: 'Landing Page' },
  { value: 'PUBLISHER_SITE', label: 'Site do Publisher' },
];

const pingFreqOptions = [
  { value: '5', label: 'A cada 5 minutos' },
  { value: '15', label: 'A cada 15 minutos' },
  { value: '30', label: 'A cada 30 minutos' },
  { value: '60', label: 'A cada 60 minutos' },
];

const seoFreqOptions = [
  { value: 'DAILY', label: 'Diario' },
  { value: 'WEEKLY', label: 'Semanal' },
];

interface ComboboxFieldProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const ComboboxField: React.FC<ComboboxFieldProps> = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = 'Selecionar...',
}) => {
  const [open, setOpen] = useState(false);
  const selectedLabel = options.find((o) => o.value === value)?.label;

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">
              {selectedLabel || placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-[0]">
          <Command>
            <CommandList>
              <CommandEmpty>Nenhuma opcao encontrada.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const AddEditMonitoredUrlModal = ({
  monitoredUrl,
  isOpen,
  toggle,
  onAddData,
  onUpdateData,
}: AddEditMonitoredUrlModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      url: monitoredUrl?.url || '',
      label: monitoredUrl?.label || '',
      type: monitoredUrl?.type || 'LANDING_PAGE',
      pingFrequency: monitoredUrl?.pingFrequency || '15',
      seoFrequency: monitoredUrl?.seoFrequency || 'WEEKLY',
      isActive: monitoredUrl?.isActive ?? true,
    },

    validationSchema: Yup.object({
      url: Yup.string().url('URL invalida').required('URL e obrigatoria'),
      label: Yup.string().optional(),
      type: Yup.string().oneOf(['LANDING_PAGE', 'PUBLISHER_SITE']).required('Tipo e obrigatorio'),
      pingFrequency: Yup.string().oneOf(['5', '15', '30', '60']).required(),
      seoFrequency: Yup.string().oneOf(['DAILY', 'WEEKLY']).required(),
    }),

    onSubmit: (values) => {
      setLoading(true);

      if (monitoredUrl) {
        updateMonitoredUrl(monitoredUrl.id, {
          label: values.label,
          pingFrequency: values.pingFrequency as any,
          seoFrequency: values.seoFrequency as any,
          isActive: values.isActive,
        })
          .then((res) => {
            toggle();
            toast.success('URL atualizada com sucesso!');
            onUpdateData(res);
          })
          .catch(() => {})
          .finally(() => setLoading(false));
      } else {
        createMonitoredUrl({
          url: values.url,
          type: values.type as any,
          label: values.label || undefined,
          pingFrequency: values.pingFrequency as any,
          seoFrequency: values.seoFrequency as any,
        })
          .then((res) => {
            toggle();
            toast.success('URL adicionada ao monitoramento!');
            onAddData(res);
          })
          .catch(() => {})
          .finally(() => setLoading(false));
      }
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) toggle(); }}>
      <DialogContent className="sm:max-w-[520px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {monitoredUrl ? 'Editar' : 'Adicionar'} URL Monitorada
            </DialogTitle>
            <DialogDescription>
              {monitoredUrl
                ? 'Atualize as configuracoes de monitoramento.'
                : 'Adicione uma nova URL para monitorar.'}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-4">
            {/* URL */}
            <div className="space-y-2">
              <Label htmlFor="url">URL *</Label>
              <Input
                type="url"
                id="url"
                name="url"
                value={formik.values.url}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="https://example.com"
                disabled={!!monitoredUrl}
                className={
                  formik.touched.url && formik.errors.url
                    ? 'border-destructive focus-visible:ring-destructive'
                    : ''
                }
              />
              {formik.touched.url && formik.errors.url && (
                <p className="text-xs text-destructive">{formik.errors.url}</p>
              )}
            </div>

            {/* Label + Tipo */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="label">Label</Label>
                <Input
                  type="text"
                  id="label"
                  name="label"
                  value={formik.values.label}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Nome amigavel (opcional)"
                />
              </div>
              <ComboboxField
                label="Tipo *"
                value={formik.values.type}
                options={typeOptions}
                onChange={(val) => formik.setFieldValue('type', val)}
                disabled={!!monitoredUrl}
                placeholder="Selecionar tipo..."
              />
            </div>

            {/* Frequencias */}
            <div className="grid grid-cols-2 gap-4">
              <ComboboxField
                label="Frequencia de Ping"
                value={formik.values.pingFrequency}
                options={pingFreqOptions}
                onChange={(val) => formik.setFieldValue('pingFrequency', val)}
                placeholder="Selecionar..."
              />
              <ComboboxField
                label="Frequencia de SEO"
                value={formik.values.seoFrequency}
                options={seoFreqOptions}
                onChange={(val) => formik.setFieldValue('seoFrequency', val)}
                placeholder="Selecionar..."
              />
            </div>

            {/* Switch isActive */}
            {monitoredUrl && (
              <div className="flex items-center justify-between rounded-lg border p-[1rem]">
                <div className="space-y-0.5">
                  <Label htmlFor="isActive">Monitoramento</Label>
                  <p className="text-xs text-muted-foreground">
                    {formik.values.isActive ? 'Ativo - verificacoes estao sendo realizadas' : 'Inativo - verificacoes pausadas'}
                  </p>
                </div>
                <Switch
                  id="isActive"
                  checked={formik.values.isActive}
                  onCheckedChange={(checked) => formik.setFieldValue('isActive', checked)}
                />
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={toggle}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {monitoredUrl ? 'Salvar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditMonitoredUrlModal;
